import { useId, useMemo, useState, type MouseEvent } from 'react'
import type { GridScenario } from '../api/grid'
import { formatCurrency, formatNumber } from '../lib/format'

export type GridPeriod = 'day' | 'week' | 'year'

export const GRID_PERIODS: {
  key: GridPeriod
  label: string
  unit: string
  field: 'hours_per_day' | 'hours_per_week' | 'hours_per_year'
}[] = [
  { key: 'day', label: 'Day', unit: 'hours / day', field: 'hours_per_day' },
  { key: 'week', label: 'Week', unit: 'hours / week', field: 'hours_per_week' },
  { key: 'year', label: 'Year', unit: 'hours / year', field: 'hours_per_year' },
]

const WIDTH = 920
const HEIGHT = 390
const MARGIN = { top: 24, right: 24, bottom: 62, left: 68 }
const PLOT_WIDTH = WIDTH - MARGIN.left - MARGIN.right
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom
const PLOT_RIGHT = MARGIN.left + PLOT_WIDTH
/** Room after the last threshold so the final step is easy to hover/click. */
const X_OVERHANG = Math.max(20, Math.round(PLOT_WIDTH * 0.035))
const X_PLOT_WIDTH = PLOT_WIDTH - X_OVERHANG

function niceCeiling(value: number): number {
  const step = value >= 5000 ? 1000 : value >= 1000 ? 500 : value >= 100 ? 50 : 10
  return Math.max(step, Math.ceil(value / step) * step)
}

/**
 * The actual COL grid as a staircase.
 *
 * The workbook does not interpolate between scenarios: each threshold holds its
 * hours until the next transaction count. This chart draws that step function and
 * lets the pointer or keyboard select a step; clicking pins it.
 */
export function GridStaircaseChart({
  scenarios,
  period,
  shopName,
}: {
  scenarios: GridScenario[]
  period: GridPeriod
  shopName: string
}) {
  const [hovered, setHovered] = useState<number | null>(null)
  const [pinned, setPinned] = useState<number | null>(null)
  const gradientId = useId().replace(/:/g, '')

  const selectedPeriod = GRID_PERIODS.find((option) => option.key === period) ?? GRID_PERIODS[0]
  const values = useMemo(
    () => scenarios.map((scenario) => scenario[selectedPeriod.field]),
    [scenarios, selectedPeriod.field],
  )
  const maxValue = Math.max(...values, 1)
  const yMax = niceCeiling(maxValue)
  const first = scenarios[0]
  const last = scenarios[scenarios.length - 1]
  const xMin = first?.transactions_per_day ?? 0
  const xMax = last?.transactions_per_day ?? xMin + 1
  const xSpan = Math.max(1, xMax - xMin)

  const x = (transactions: number) =>
    MARGIN.left + ((transactions - xMin) / xSpan) * X_PLOT_WIDTH
  const y = (value: number) =>
    MARGIN.top + PLOT_HEIGHT - (Math.max(0, value) / yMax) * PLOT_HEIGHT

  const stepPath = useMemo(() => {
    if (scenarios.length === 0) return ''
    const points = scenarios.map((scenario, index) => ({
      x: x(scenario.transactions_per_day),
      y: y(values[index]),
    }))
    let path = `M ${points[0].x} ${points[0].y}`
    for (let index = 1; index < points.length; index += 1) {
      path += ` H ${points[index].x} V ${points[index].y}`
    }
    // The grid's last scenario also applies above the maximum threshold.
    path += ` H ${PLOT_RIGHT}`
    return path
  }, [scenarios, values, yMax])

  const areaPath = useMemo(() => {
    if (scenarios.length === 0) return ''
    const base = MARGIN.top + PLOT_HEIGHT
    const points = scenarios.map((scenario, index) => ({
      x: x(scenario.transactions_per_day),
      y: y(values[index]),
    }))
    let path = `M ${points[0].x} ${base} L ${points[0].x} ${points[0].y}`
    for (let index = 1; index < points.length; index += 1) {
      path += ` H ${points[index].x} V ${points[index].y}`
    }
    path += ` H ${PLOT_RIGHT} L ${PLOT_RIGHT} ${base} Z`
    return path
  }, [scenarios, values, yMax])

  const activeIndex = hovered ?? pinned ?? 0
  const active = scenarios[activeIndex] ?? scenarios[0]

  const yTicks = Array.from({ length: 5 }, (_, index) => (yMax / 4) * index)
  const xTicks = Array.from({ length: 5 }, (_, index) => xMin + (xSpan / 4) * index)

  if (!active) return null

  const selectStep = (index: number) => {
    setPinned((current) => (current === index ? null : index))
    setHovered(index)
  }

  // The grid's revenue and transaction columns scale with the selected period.
  // Keep the step detail in the same unit as the chart instead of always
  // showing daily figures.
  const periodMultiplier = period === 'week' ? 7 : period === 'year' ? 52 : 1
  const revenueUnit =
    period === 'week' ? 'revenue/week' : period === 'year' ? 'revenue/year' : 'revenue/day'
  const transactionUnit =
    period === 'week'
      ? 'transactions/week'
      : period === 'year'
        ? 'transactions/year'
        : 'transactions/day'
  const revenueForPeriod = active.revenue_per_day_eur * periodMultiplier
  const transactionsForPeriod = active.transactions_per_day * periodMultiplier

  /**
   * The whole plot is the hit target, not just the staircase stroke.
   * Convert the pointer position into SVG coordinates, then find the last
   * threshold at or before it — that is the active step for that x position.
   */
  const handleChartMove = (event: MouseEvent<SVGSVGElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect()
    if (bounds.width <= 0 || bounds.height <= 0) return

    const chartX = ((event.clientX - bounds.left) / bounds.width) * WIDTH
    const chartY = ((event.clientY - bounds.top) / bounds.height) * HEIGHT
    const insidePlot =
      chartX >= MARGIN.left &&
      chartX <= MARGIN.left + PLOT_WIDTH &&
      chartY >= MARGIN.top &&
      chartY <= MARGIN.top + PLOT_HEIGHT

    if (!insidePlot) {
      setHovered(null)
      return
    }

    const transaction = xMin + ((chartX - MARGIN.left) / X_PLOT_WIDTH) * xSpan
    let index = 0
    for (let candidate = 0; candidate < scenarios.length; candidate += 1) {
      if (scenarios[candidate].transactions_per_day <= transaction + 1e-9) index = candidate
    }
    setHovered(index)
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-slate-200/80 bg-slate-50/60 px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.03]">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            Step detail · {shopName}
          </p>
          <p className="mt-1 text-sm font-semibold tabular-nums text-slate-900 dark:text-white">
            {formatNumber(transactionsForPeriod, 0)} {transactionUnit}
            <span className="mx-1.5 font-normal text-slate-300 dark:text-slate-600">·</span>
            {formatCurrency(revenueForPeriod)} {revenueUnit}
          </p>
        </div>
        <div className="flex flex-wrap items-baseline gap-2 text-right">
          {GRID_PERIODS.map((option) => {
            const value = active[option.field]
            const isActive = option.key === period
            return (
              <span
                key={option.key}
                className={`rounded-lg px-2 py-1 text-xs tabular-nums ${
                  isActive
                    ? 'bg-salsa-500/10 font-semibold text-salsa-700 dark:text-salsa-300'
                    : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                {formatNumber(value, 1)}
                <span className="ml-1 text-[10px] font-normal opacity-75">{option.unit}</span>
              </span>
            )
          })}
        </div>
      </div>

      <div className="relative">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="h-auto w-full overflow-visible"
          role="group"
          aria-label={`COL grid for ${shopName}, ${selectedPeriod.unit}`}
          onMouseMove={handleChartMove}
          onMouseLeave={() => setHovered(null)}
        >
          <title>{`COL grid staircase for ${shopName}`}</title>
          <desc>
            Grid hours by daily transactions. Use Tab to move through the steps, Enter or
            Space to pin one.
          </desc>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(249 115 22)" stopOpacity="0.24" />
              <stop offset="100%" stopColor="rgb(249 115 22)" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {yTicks.map((tick) => (
            <g key={`y-${tick}`}>
              <line
                x1={MARGIN.left}
                x2={MARGIN.left + PLOT_WIDTH}
                y1={y(tick)}
                y2={y(tick)}
                className="stroke-slate-200/80 dark:stroke-white/10"
                strokeDasharray={tick === 0 ? undefined : '3 5'}
              />
              <text
                x={MARGIN.left - 12}
                y={y(tick) + 4}
                textAnchor="end"
                className="fill-slate-400 text-[11px] tabular-nums dark:fill-slate-500"
              >
                {formatNumber(tick, 0)}
              </text>
            </g>
          ))}

          {xTicks.map((tick) => (
            <g key={`x-${tick}`}>
              <line
                x1={x(tick)}
                x2={x(tick)}
                y1={MARGIN.top}
                y2={MARGIN.top + PLOT_HEIGHT}
                className="stroke-slate-200/50 dark:stroke-white/5"
              />
              <text
                x={x(tick)}
                y={MARGIN.top + PLOT_HEIGHT + 22}
                textAnchor="middle"
                className="fill-slate-400 text-[11px] tabular-nums dark:fill-slate-500"
              >
                {formatNumber(tick, 0)}
              </text>
            </g>
          ))}

          <text
            x={MARGIN.left + PLOT_WIDTH / 2}
            y={HEIGHT - 8}
            textAnchor="middle"
            className="fill-slate-400 text-[11px] font-semibold uppercase tracking-wider dark:fill-slate-500"
          >
            Transactions per day
          </text>
          <text
            transform={`translate(16 ${MARGIN.top + PLOT_HEIGHT / 2}) rotate(-90)`}
            textAnchor="middle"
            className="fill-slate-400 text-[11px] font-semibold uppercase tracking-wider dark:fill-slate-500"
          >
            {`Hours per ${period}`}
          </text>

          <path d={areaPath} fill={`url(#${gradientId})`} />
          <path
            d={stepPath}
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinejoin="round"
            className="text-salsa-500"
          />

          {scenarios.map((scenario, index) => {
            const next = scenarios[index + 1]
            const segmentY = y(values[index])
            const segmentX = x(scenario.transactions_per_day)
            const segmentWidth = (next ? x(next.transactions_per_day) : PLOT_RIGHT) - segmentX
            return (
              <rect
                key={`hit-${scenario.transactions_per_day}`}
                x={segmentX}
                y={segmentY - 14}
                width={Math.max(1, segmentWidth)}
                height={28}
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() => setHovered(index)}
                onClick={() => selectStep(index)}
              />
            )
          })}

          {scenarios.map((scenario, index) => {
            const value = values[index]
            const isActive = index === activeIndex
            const pointX = x(scenario.transactions_per_day)
            const pointY = y(value)
            return (
              <circle
                key={`point-${scenario.transactions_per_day}`}
                cx={pointX}
                cy={pointY}
                r={isActive ? 6 : 3}
                tabIndex={0}
                role="button"
                aria-pressed={pinned === index}
                aria-label={`${formatNumber(
                  scenario.transactions_per_day,
                  0,
                )} transactions per day: ${formatNumber(value, 1)} ${selectedPeriod.unit}`}
                className={`cursor-pointer stroke-white outline-none transition-[r] focus-visible:stroke-salsa-700 dark:stroke-ink-950 ${
                  isActive ? 'fill-salsa-500' : 'fill-salsa-400 dark:fill-salsa-500/70'
                }`}
                strokeWidth={2}
                onMouseEnter={() => setHovered(index)}
                onFocus={() => setHovered(index)}
                onBlur={() => setHovered(null)}
                onClick={() => selectStep(index)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    selectStep(index)
                  }
                }}
              />
            )
          })}

          {active && (
            <line
              x1={x(active.transactions_per_day)}
              x2={x(active.transactions_per_day)}
              y1={MARGIN.top}
              y2={MARGIN.top + PLOT_HEIGHT}
              className="pointer-events-none stroke-salsa-500/50"
              strokeDasharray="4 4"
            />
          )}
        </svg>
      </div>

      <p className="text-[10px] leading-relaxed text-slate-400 dark:text-slate-500">
        Hover a step or use Tab to inspect it; click or press Enter to pin it. The grid is a
        staircase — hours hold steady until the next transaction threshold, exactly as in the
        workbook. Week = 7 days · year = 52 weeks.
      </p>
    </div>
  )
}
